import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Scales, Sparkle, Timer, UserCircle, CalendarBlank, WarningOctagon,
  CurrencyDollar, ListChecks, ShieldCheck, ArrowRight, Copy, CaretDown, CaretUp
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_URL}/api/legal-demo` });

// NoteCrunch Legal — standalone pitch demo for legal-migration platforms.
// One job: messy attorney note in → structured destination fields out, fast.

function EntityCard({ icon: Icon, title, children, testId }) {
  return (
    <div className="bg-white/[0.04] border border-white/10 rounded-lg p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2.5">
        <Icon size={16} weight="fill" className="text-[#C9A44C]" />
        <h4 className="text-xs font-black uppercase tracking-widest text-white/60">{title}</h4>
      </div>
      {children}
    </div>
  );
}

export default function NoteCrunchLegalPage() {
  const [samples, setSamples] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [out, setOut] = useState(null);          // {processing_seconds, result, integrity}
  const [destTab, setDestTab] = useState('clio');
  const [showJson, setShowJson] = useState(false);
  const [elapsedLive, setElapsedLive] = useState(0);
  const timerRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    api.get('/samples').then(({ data }) => setSamples(data.samples || [])).catch(() => {});
  }, []);

  const runExtract = async () => {
    if (busy || note.trim().length < 40) return;
    setBusy(true); setError(''); setOut(null); setElapsedLive(0);
    const t0 = performance.now();
    timerRef.current = setInterval(() => setElapsedLive((performance.now() - t0) / 1000), 100);
    try {
      const { data } = await api.post('/extract', { note });
      setOut(data);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    } catch (err) {
      setError(err.response?.data?.detail || 'Extraction failed — try again.');
    } finally {
      clearInterval(timerRef.current);
      setBusy(false);
    }
  };

  const r = out?.result || {};
  const destFields = destTab === 'clio' ? (r.clio_fields || {}) : (r.mycase_fields || {});

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" style={{ fontFamily: 'Inter, system-ui, sans-serif' }} data-testid="notecrunch-legal-page">
      {/* Top bar */}
      <header className="border-b border-white/10 px-6 py-4 sticky top-0 bg-[#0B0E14]/95 backdrop-blur-sm z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5" data-testid="ncl-logo">
            <Scales size={26} weight="fill" className="text-[#C9A44C]" />
            <span className="text-lg font-black tracking-tight">NoteCrunch <span className="text-[#C9A44C]">Legal</span></span>
            <span className="hidden sm:inline-block bg-[#C9A44C]/15 border border-[#C9A44C]/40 text-[#C9A44C] text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-wider">Patent Pending</span>
          </div>
          <a
            href="mailto:chip@migrationcrunch.com?subject=NoteCrunch%20Legal%20—%20licensing%20inquiry"
            className="bg-[#C9A44C] hover:bg-[#B8933B] text-black px-4 py-2 text-xs font-black rounded transition-colors"
            data-testid="ncl-licensing-cta"
          >
            Licensing inquiries →
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 pt-14 pb-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-4" data-testid="ncl-headline">
            Ten years of attorney notes.<br /><span className="text-[#C9A44C]">Structured in seconds.</span>
          </h1>
          <p className="text-white/65 text-base md:text-lg leading-relaxed">
            Legal data migrations move the schema cleanly — then dump a decade of free-text case notes
            into the new system as unsearchable blobs. Our patent-pending engine reads every note,
            extracts the judges, deadlines, co-counsel, and dollar amounts, and maps them into
            structured destination fields. The original note is always preserved, byte-for-byte.
          </p>
        </div>
      </section>

      {/* Demo video */}
      <section className="px-6 pb-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white/[0.03] border border-[#C9A44C]/35 rounded-xl p-3 md:p-4" data-testid="ncl-video-card">
            <video
              src="/videos/notecrunch-legal-demo.mp4"
              controls
              preload="metadata"
              className="w-full rounded-lg bg-black"
              data-testid="ncl-video"
            >
              Your browser does not support video playback.
            </video>
            <p className="text-[11px] text-white/40 text-center mt-2.5">
              95 seconds — a real messy case note becomes structured Clio &amp; MyCase fields, live.
            </p>
          </div>
        </div>
      </section>

      {/* Demo */}
      <section className="px-6 pb-16">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5 md:p-7">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h2 className="text-lg font-black flex items-center gap-2">
                <Sparkle size={18} weight="fill" className="text-[#C9A44C]" /> Try it — paste a messy note
              </h2>
              <div className="flex flex-wrap gap-2">
                {samples.map(s => (
                  <button
                    key={s.id}
                    onClick={() => { setNote(s.note); setOut(null); setError(''); }}
                    className="text-[11px] font-bold border border-white/20 hover:border-[#C9A44C]/60 hover:text-[#C9A44C] text-white/60 px-3 py-1.5 rounded-full transition-colors"
                    data-testid={`ncl-sample-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={'Paste a raw attorney case note here — the messier the better. Or click a sample above.'}
              rows={7}
              className="w-full bg-black/40 border border-white/15 focus:border-[#C9A44C]/60 rounded-lg p-4 text-sm text-white/85 leading-relaxed focus:outline-none resize-y placeholder:text-white/25"
              data-testid="ncl-note-input"
            />

            <div className="flex items-center justify-between flex-wrap gap-3 mt-4">
              <p className="text-[11px] text-white/35">{note.trim().length.toLocaleString()} characters · nothing is stored except run stats</p>
              <button
                onClick={runExtract}
                disabled={busy || note.trim().length < 40}
                className="inline-flex items-center gap-2 bg-[#C9A44C] hover:bg-[#B8933B] disabled:opacity-40 text-black px-6 py-2.5 text-sm font-black rounded transition-colors"
                data-testid="ncl-extract-btn"
              >
                {busy ? (
                  <><Timer size={16} weight="fill" className="animate-pulse" /> Extracting… {elapsedLive.toFixed(1)}s</>
                ) : (
                  <>Extract the structure <ArrowRight size={15} weight="bold" /></>
                )}
              </button>
            </div>
            {error && <p className="text-xs text-red-400 font-medium mt-3" data-testid="ncl-error">{error}</p>}
          </div>

          {/* Results */}
          {out && (
            <div ref={resultsRef} className="mt-6 space-y-4" data-testid="ncl-results">
              {/* Header strip */}
              <div className="flex items-center flex-wrap gap-3 bg-emerald-500/[0.08] border border-emerald-400/30 rounded-lg px-4 py-3">
                <span className="inline-flex items-center gap-1.5 text-emerald-300 font-black text-sm" data-testid="ncl-timer">
                  <Timer size={16} weight="fill" /> {out.processing_seconds}s
                </span>
                <span className="text-white/60 text-xs">
                  {out.integrity.characters_in.toLocaleString()} characters in · <strong className="text-white/85">{out.integrity.entities_extracted} entities extracted</strong> · original note preserved
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-300/90" data-testid="ncl-zero-retention">
                  <ShieldCheck size={13} weight="fill" /> Zero-retention — processed in memory, never stored
                </span>
                {r.matter_type && <span className="ml-auto bg-white/10 text-white/75 text-[11px] font-bold px-2.5 py-1 rounded">{r.matter_type}</span>}
              </div>

              {r.summary && (
                <p className="text-sm text-white/70 italic px-1" data-testid="ncl-summary">&ldquo;{r.summary}&rdquo;</p>
              )}

              {/* Entity grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(r.people || []).length > 0 && (
                  <EntityCard icon={UserCircle} title={`People & roles (${r.people.length})`} testId="ncl-people">
                    <ul className="space-y-2">
                      {r.people.map((p, i) => (
                        <li key={i} className="text-sm">
                          <span className="font-bold">{p.name}</span>
                          {p.role && <span className="ml-2 text-[10px] uppercase tracking-wider font-black text-[#C9A44C] bg-[#C9A44C]/10 px-1.5 py-0.5 rounded">{p.role}</span>}
                          <div className="text-xs text-white/50">
                            {[p.organization, p.phone, p.email].filter(Boolean).join(' · ')}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </EntityCard>
                )}
                {(r.deadlines || []).length > 0 && (
                  <EntityCard icon={WarningOctagon} title={`Deadlines (${r.deadlines.length})`} testId="ncl-deadlines">
                    <ul className="space-y-2">
                      {r.deadlines.map((d, i) => (
                        <li key={i} className="text-sm flex items-start gap-2">
                          <span className={`font-black shrink-0 ${d.critical ? 'text-red-400' : 'text-white/85'}`}>{d.date}</span>
                          <span className="text-white/60 text-xs leading-relaxed">{d.description}{d.critical && <span className="ml-1.5 text-red-400 font-black text-[10px] uppercase">critical</span>}</span>
                        </li>
                      ))}
                    </ul>
                  </EntityCard>
                )}
                {(r.dates || []).length > 0 && (
                  <EntityCard icon={CalendarBlank} title={`Key dates (${r.dates.length})`} testId="ncl-dates">
                    <ul className="space-y-1.5">
                      {r.dates.map((d, i) => (
                        <li key={i} className="text-sm"><span className="font-bold">{d.date}</span> <span className="text-white/55 text-xs">— {d.event}</span></li>
                      ))}
                    </ul>
                  </EntityCard>
                )}
                {(r.amounts || []).length > 0 && (
                  <EntityCard icon={CurrencyDollar} title={`Amounts (${r.amounts.length})`} testId="ncl-amounts">
                    <ul className="space-y-1.5">
                      {r.amounts.map((a, i) => (
                        <li key={i} className="text-sm"><span className="font-bold text-emerald-300">{a.amount}</span> <span className="text-white/55 text-xs">— {a.context}</span></li>
                      ))}
                    </ul>
                  </EntityCard>
                )}
                {(r.action_items || []).length > 0 && (
                  <EntityCard icon={ListChecks} title={`Action items (${r.action_items.length})`} testId="ncl-actions">
                    <ul className="space-y-1.5">
                      {r.action_items.map((a, i) => (
                        <li key={i} className="text-sm text-white/75 flex gap-2"><span className="text-[#C9A44C]">→</span>{a}</li>
                      ))}
                    </ul>
                  </EntityCard>
                )}
              </div>

              {/* Destination fields */}
              <div className="bg-white/[0.04] border border-white/10 rounded-lg p-5" data-testid="ncl-dest-fields">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                  <h3 className="text-sm font-black uppercase tracking-widest text-white/60">Mapped destination fields</h3>
                  <div className="flex gap-1.5">
                    {['clio', 'mycase'].map(t => (
                      <button
                        key={t}
                        onClick={() => setDestTab(t)}
                        className={`text-[11px] font-black px-3 py-1.5 rounded transition-colors ${destTab === t ? 'bg-[#C9A44C] text-black' : 'bg-white/8 text-white/50 hover:text-white/80'}`}
                        data-testid={`ncl-dest-tab-${t}`}
                      >
                        {t === 'clio' ? 'Clio-style' : 'MyCase-style'}
                      </button>
                    ))}
                  </div>
                </div>
                {Object.keys(destFields).length === 0 ? (
                  <p className="text-xs text-white/40">No mapped fields for this destination.</p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(destFields).map(([k, v]) => (
                        <tr key={k} className="border-t border-white/8">
                          <td className="py-2 pr-4 text-white/50 text-xs font-bold whitespace-nowrap align-top">{k}</td>
                          <td className="py-2 text-white/85">{String(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Raw JSON */}
              <div>
                <button
                  onClick={() => setShowJson(s => !s)}
                  className="inline-flex items-center gap-1.5 text-xs text-white/45 hover:text-white/75 font-bold"
                  data-testid="ncl-json-toggle"
                >
                  {showJson ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />} Raw JSON (what your pipeline receives)
                </button>
                {showJson && (
                  <div className="relative mt-2">
                    <button
                      onClick={() => navigator.clipboard.writeText(JSON.stringify(r, null, 2))}
                      className="absolute top-2 right-2 text-white/40 hover:text-white/80"
                      title="Copy"
                      data-testid="ncl-json-copy"
                    >
                      <Copy size={15} />
                    </button>
                    <pre className="bg-black/50 border border-white/10 rounded-lg p-4 text-[11px] text-emerald-200/80 overflow-x-auto max-h-80 overflow-y-auto" data-testid="ncl-json">
{JSON.stringify(r, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* How it fits */}
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-black text-center mb-8">Built to sit inside a migration pipeline</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { n: '1', t: 'Your migration runs as usual', b: 'Your platform moves the schema — matters, contacts, documents — exactly like it does today. We touch nothing.' },
              { n: '2', t: 'Note fields route through our API', b: 'Each free-text note makes one metered API call. Entities and destination-field mappings come back in seconds, ready to write.' },
              { n: '3', t: 'The destination gets both', b: 'Structured fields the new system can search and automate — plus the original note preserved verbatim, integrity-verified.' },
            ].map(s => (
              <div key={s.n} className="bg-white/[0.03] border border-white/10 rounded-lg p-5" data-testid={`ncl-fit-${s.n}`}>
                <div className="w-8 h-8 rounded-full bg-[#C9A44C]/15 border border-[#C9A44C]/40 flex items-center justify-center text-[#C9A44C] font-black text-sm mb-3">{s.n}</div>
                <h3 className="font-black text-sm mb-1.5">{s.t}</h3>
                <p className="text-xs text-white/55 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-start gap-3 bg-white/[0.03] border border-white/10 rounded-lg p-5" data-testid="ncl-integrity-note">
            <ShieldCheck size={22} weight="fill" className="text-emerald-300 shrink-0 mt-0.5" />
            <p className="text-sm text-white/65 leading-relaxed">
              <strong className="text-white/90">Built for privilege-sensitive data.</strong> Every migrated note ships with a
              cryptographically verified round-trip check — the destination copy is proven byte-identical to the source, and the
              audit certificate says so in writing. Compliance teams don&apos;t have to take anyone&apos;s word for it.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto text-center bg-gradient-to-r from-[#C9A44C]/12 to-transparent border border-[#C9A44C]/40 rounded-xl p-8">
          <h2 className="text-2xl font-black mb-2">Send us your ugliest notes export.</h2>
          <p className="text-white/65 text-sm mb-6">We&apos;ll return it structured — with the audit certificate — within one business day. That&apos;s the whole pitch.</p>
          <a
            href="mailto:chip@migrationcrunch.com?subject=NoteCrunch%20Legal%20—%20ugly%20file%20challenge"
            className="inline-flex items-center gap-2 bg-[#C9A44C] hover:bg-[#B8933B] text-black px-8 py-3 text-sm font-black rounded transition-colors"
            data-testid="ncl-challenge-cta"
          >
            Take the ugly-file challenge <ArrowRight size={15} weight="bold" />
          </a>
        </div>
      </section>

      <footer className="border-t border-white/10 px-6 py-6 text-center">
        <p className="text-[11px] text-white/35">
          NoteCrunch Legal · Patent-pending AI note extraction for legal data migrations · White-label &amp; API licensing available ·{' '}
          <a href="mailto:chip@migrationcrunch.com" className="underline hover:text-white/60">chip@migrationcrunch.com</a>
        </p>
      </footer>
    </div>
  );
}
